import { useCallback, useEffect, useSyncExternalStore } from "react";
import { useAuth } from "./AuthProvider";
import { passkeysPossible, usableHere, type CognitoApi } from "./cognito-api";
import { useCognitoApi } from "./useCognitoApi";

/**
 * How many passkeys work on this site (PLT-06; the nudge template's one
 * client-side fact): Cognito lists them only for the person's own access
 * token, so the browser asks, once per token. A passkey enrolled for
 * another relying party is listed but useless here (pitfall 17), so it
 * does not count. `undefined` while unknown — and after a failure — so
 * the nudge waits rather than flashing.
 *
 * A tiny store rather than React Query: the Security page, the banner and
 * every component test share it without a provider to mount.
 */
let cached: { token: string; count?: number | undefined; loading: boolean } | undefined;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

function load(api: CognitoApi, token: string): void {
  // Once per token: loading, answered or failed (a failure stays unknown until the Security page invalidates it).
  if (cached?.token === token) return;
  cached = { token, loading: true };
  api
    .listPasskeys(token)
    .then((r) => {
      if (cached?.token === token) cached = { token, loading: false, count: (r.Credentials ?? []).filter((c) => usableHere(c)).length };
    })
    .catch(() => {
      if (cached?.token === token) cached = { token, loading: false };
    })
    .finally(notify);
}

export function usePasskeyCount(): number | undefined {
  const { config, session, me } = useAuth();
  const api = useCognitoApi(config);
  const token = session?.accessToken;
  // A federated person's passkeys are their identity provider's; nothing to ask Cognito.
  const enabled = !!token && !!me && !me.identityProvider && passkeysPossible();
  const count = useSyncExternalStore(subscribe, () => (enabled && cached?.token === token ? cached.count : undefined));
  const version = useSyncExternalStore(subscribe, () => cached);
  useEffect(() => {
    if (enabled && token) load(api, token);
  }, [enabled, api, token, version]);
  return count;
}

/** Forget the count, so the next reader asks Cognito again. */
export function forgetPasskeyCount(): void {
  cached = undefined;
  notify();
}

/** Called after an enrol or a removal on the Security page, so the banner follows at once. */
export function useInvalidatePasskeys(): () => void {
  return useCallback(() => forgetPasskeyCount(), []);
}
